import { createTRPCRouter, protectedProcedure } from "../trpc"
import { z } from "zod"
import { Record } from "@prisma/client/runtime/library"
import { getNepaliMonthIndex, sortByNepaliDate } from "../utils"

export type PurposeCategory = "INCOME" | "EXPENSE" | "OTHER"
export type PaymentMethod = "Cash" | "Online" | "Cheque"
export type TimeFrame = "day" | "month" | "year"

export const transactionRouter = createTRPCRouter({
    getAllYear: protectedProcedure
        .query(({ ctx }) => {
            return ctx.prisma.transaction.findMany({
                select: {
                    year: true,
                },
                distinct: ['year'],
                orderBy: {
                    year: 'asc'
                }
            })
        }),

    getSummaryData: protectedProcedure
        .input(z.object({
            year: z.number(),
            month: z.string().optional(),
            day: z.number().optional(),
        }))
        .query(async ({ ctx, input }) => {
            const filter: any = { isActive: true };
            if (input.year) filter.year = input.year;
            if (input.month) filter.month = input.month;
            if (input.day) filter.day = input.day;

            // Fetch filtered transactions
            const transactions = await ctx.prisma.transaction.findMany({
                where: filter,
            });
            // sort by nepali date
            transactions.sort((a, b) => sortByNepaliDate(a, b));

            // Initialize summary structure with categories
            const summary: Record<PurposeCategory, Record<string, any>> = {
                INCOME: {},
                EXPENSE: {},
                OTHER: {}
            };

            // Initialize method totals for each category
            const categoryTotals: Record<PurposeCategory, {
                Cash: number;
                Online: number;
                Cheque: number;
                total: number;
            }> = {
                INCOME: { Cash: 0, Online: 0, Cheque: 0, total: 0 },
                EXPENSE: { Cash: 0, Online: 0, Cheque: 0, total: 0 },
                OTHER: { Cash: 0, Online: 0, Cheque: 0, total: 0 }
            };

            // Process each transaction
            transactions.forEach(transaction => {
                const { type, purpose, method, amount } = transaction;
                const category = type as PurposeCategory;

                // Initialize purpose if it doesn't exist
                if (!summary[category][purpose]) {
                    summary[category][purpose] = {
                        Cash: 0,
                        Online: 0,
                        Cheque: 0,
                        total: 0
                    };
                }

                // Add amount to the purpose by payment method
                summary[category][purpose][method] += amount;
                summary[category][purpose].total += amount;

                // Update category totals
                categoryTotals[category][method as PaymentMethod] += amount;
                categoryTotals[category].total += amount;
            });

            // Add totals to the summary
            Object.keys(summary).forEach(category => {
                const typedCategory = category as PurposeCategory;
                summary[typedCategory] = {
                    ...summary[typedCategory],
                    total: categoryTotals[typedCategory]
                };
            });

            //  USE THIS IF YOU SOMEDAY ADDED A MONTH_INDEX TO THE DATABASE OR YOU ARE BURNING SERVERS
            //
            // // FOR OPENING BALANCE: Get all transactions before the current period
            // const openingBalanceFilter: Record<string, any> = { isActive: true };
            //
            // if (input.year) {
            //     if (input.month) {
            //         if (input.day) {
            //             // IF WE HAVE YEAR, MONTH AND DAY : WE WANT ALL THE TRANSACTION BEFORE THAT DATE
            //             // basically saying transactions before the given year, or given year but before given month 
            //             // or given year or month but before given day
            //             openingBalanceFilter.OR = [
            //                 { year: { lt: input.year } },
            //                 {
            //                     year: input.year,
            //                     month: { lt: input.month }
            //                 },
            //                 {
            //                     year: input.year,
            //                     month: input.month,
            //                     day: { lt: input.day }
            //                 }
            //             ];
            //         } else {
            //             // For specific month
            //             openingBalanceFilter.OR = [
            //                 { year: { lt: input.year } },
            //                 {
            //                     year: input.year,
            //                     month: { lt: input.month }
            //                 }
            //             ];
            //         }
            //     } else {
            //         // For specific year
            //         openingBalanceFilter.year = { lt: input.year };
            //     }
            // }
            //
            // // Check if there are any prior transactions
            // const priorTransactionsCount = await ctx.prisma.transaction.count({
            //     where: openingBalanceFilter
            // });
            //
            // console.log(priorTransactionsCount)
            //
            // let openingBalance = 0;
            // if (priorTransactionsCount > 0) {
            //     const incomeAggregate = await ctx.prisma.transaction.aggregate({
            //         _sum: { amount: true },
            //         where: {
            //             ...openingBalanceFilter,
            //             type: 'INCOME'
            //         }
            //     });
            //
            //     const expenseAggregate = await ctx.prisma.transaction.aggregate({
            //         _sum: { amount: true },
            //         where: {
            //             ...openingBalanceFilter,
            //             type: 'EXPENSE'
            //         }
            //     });
            //
            //     const InvestmentAggregate = await ctx.prisma.transaction.aggregate({
            //         _sum: { amount: true },
            //         where: {
            //             ...openingBalanceFilter,
            //             type: 'OTHER',
            //             purpose: 'Investment'
            //         }
            //     });
            //
            //     //other types of OTHER transactions : Like Previous Months
            //     // const otherNonInvestmentAggregate = await ctx.prisma.transaction.aggregate({
            //     //     _sum: { amount: true },
            //     //     where: {
            //     //         ...openingBalanceFilter,
            //     //         type: 'OTHER',
            //     //         purpose: { not: 'Investment' }
            //     //     }
            //     // });
            //
            //     // Calculate opening balance
            //     openingBalance =
            //         (incomeAggregate._sum.amount || 0) -
            //         (expenseAggregate._sum.amount || 0) +
            //         (InvestmentAggregate._sum.amount || 0)
            //     // + (otherNonInvestmentAggregate._sum.amount || 0);
            // }


            // REMOVE FROM HERE - IF YOU ARE USING UPPER METHOD OF DOING 
            // THIS IS MEMORY HUNGRY CODE BTW, DON'T TRY THIS AT HOME
            // Fetch ALL transactions for opening balance calculation
            const allTransactions = await ctx.prisma.transaction.findMany({
                where: { isActive: true }
            });

            // Sort all transactions by Nepali date
            allTransactions.sort((a, b) => sortByNepaliDate(a, b));

            // Determine cutoff point for opening balance
            const cutoffDate = {
                year: input.year,
                month: input.month || "",
                day: input.day || 1
            };

            // Filter transactions for opening balance - all transactions before the current period
            const priorTransactions = allTransactions.filter(transaction => {
                // Skip transactions that aren't for the current period
                if (!isBeforeDate(transaction, cutoffDate)) {
                    return false;
                }
                return true;
            });

            // Calculate opening balance
            let openingBalance = 0;
            if (priorTransactions.length > 0) {
                priorTransactions.forEach(transaction => {
                    const { type, purpose, amount } = transaction;

                    if (type === 'INCOME') {
                        openingBalance += amount;
                    } else if (type === 'EXPENSE') {
                        openingBalance -= amount;
                    } else if (type === 'OTHER') {
                        if (purpose === 'Investment') {
                            openingBalance += amount;
                        } else {
                            // Handle other purposes if needed
                            // openingBalance += amount;
                        }
                    }
                });
            }

            let currentPeriodChange = 0;
            // calculate current period change on balance
            transactions.forEach(transaction => {
                const { type, purpose, amount } = transaction;

                if (type === 'INCOME') {
                    currentPeriodChange += amount;
                } else if (type === 'EXPENSE') {
                    currentPeriodChange -= amount;
                } else if (type === 'OTHER') {
                    if (purpose === 'Investment') {
                        currentPeriodChange += amount;
                    } else {
                        // Handle other purposes if needed
                        // currentPeriodChange += amount;
                    }
                }
            });

            // TODO:
            // previous month = opening balance - Investment

            const closingBalance = openingBalance + currentPeriodChange

            // Get the latest transaction date for reference
            const latestTransactionDate = transactions.length > 0 ? transactions[transactions.length - 1] : null;

            return {
                ...summary,
                balances: {
                    opening: openingBalance,
                    closing: closingBalance,
                },
                latestTransaction: latestTransactionDate
            };
        }),
});


// SO TRICKY !!!!!!!!!!!!!!!!!!!!!!!!!! to work with nepali months
function isBeforeDate(
    transaction: { year: number | null, month: string | null, day: number | null },
    cutoffDate: { year: number, month: string, day: number }
): boolean {
    // If transaction has no date information, treat it as before (default behavior)
    if (!transaction.year || !transaction.month) return true;

    // Compare by year first
    if (transaction.year < cutoffDate.year) return true;
    if (transaction.year > cutoffDate.year) return false;

    // Same year, compare by month
    const transactionMonthIndex = getNepaliMonthIndex(transaction.month);
    const cutoffMonthIndex = getNepaliMonthIndex(cutoffDate.month);

    if (transactionMonthIndex < cutoffMonthIndex) return true;
    if (transactionMonthIndex > cutoffMonthIndex) return false;

    // Same year and month, compare by day
    const transactionDay = transaction.day || 1;
    return transactionDay < cutoffDate.day;
}
